// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel';

import { LocationId } from './LocationId';
import { TemperatureThreshold } from './TemperatureThreshold';
import { Carton } from './Carton';
import { LineItemPart } from './LineItemPart';
import { MasterLocation } from './MasterLocation';
import { SkillId } from './SkillId';

import type { LineItemPartModelType } from './LineItemPart';
import type { LocationIdModelType } from './LocationId';
import type { MasterLocationModelType } from './MasterLocation';
import type { TemperatureThresholdModelType } from './TemperatureThreshold';
import type { CartonModelType } from './Carton';
import type { Volume } from './Volume';
import type { AuditMetadataModelType } from './AuditMetadata';

type StatusEnum = 'ACTIVE' | 'INACTIVE';

export type SummaryType = {
    totalVolume: Volume,
    totalWeight: number
};

export type MasterLineItemModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    status: StatusEnum,
    description: ?string,
    parts: Array<LineItemPartModelType>,
    scanId: ?string,
    category: ?string,
    imageUrl: ?string,
    locationIds: Array<LocationIdModelType>,
    locations: Array<MasterLocationModelType>,
    instructions: Array<string>,
    temperatureThreshold: ?TemperatureThresholdModelType,
    transactionDuration: ?number,
    carton: ?CartonModelType,
    shouldCrate: boolean,
    customProperties: { [key: string]: string },
    version: number,
    summary: SummaryType,
    skills: Array<SkillIdModelType>,
    auditMetadata: ?AuditMetadataModelType
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterLineItem extends ImmutableModel {
    static fromJS(json: MasterLineItemModelType): MasterLineItem {
        const state: Object = Object.assign({}, json);
        state.parts = Immutable.List(state.parts).map(item =>
            LineItemPart.fromJS(item)
        );
        state.locationIds = Immutable.List(state.locationIds).map(item =>
            LocationId.fromJS(item)
        );
        state.locations = Immutable.List(state.locations).map(item =>
            MasterLocation.fromJS(item)
        );
        state.instructions = Immutable.List(state.instructions);
        state.temperatureThreshold = state.temperatureThreshold
            ? TemperatureThreshold.fromJS(state.temperatureThreshold)
            : state.temperatureThreshold;
        state.carton = state.carton
            ? Carton.fromJS(state.carton)
            : state.carton;
        state.customProperties = Immutable.Map(state.customProperties);
        state.skills = Immutable.List(state.skills).map(item =>
            SkillId.fromJS(item)
        );
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        return new this(Immutable.Map(state));
    }

    toJS(): MasterLineItemModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            status: this.status,
            description: this.description,
            parts: this.parts.toArray().map(item => item.toJS()),
            scanId: this.scanId,
            category: this.category,
            imageUrl: this.imageUrl,
            locationIds: this.locationIds.toArray().map(item => item.toJS()),
            locations: this.locations.toArray().map(item => item.toJS()),
            instructions: this.instructions.toArray(),
            temperatureThreshold: this.temperatureThreshold
                ? this.temperatureThreshold.toJS()
                : this.temperatureThreshold,
            transactionDuration: this.transactionDuration,
            carton: this.carton ? this.carton.toJS() : this.carton,
            shouldCrate: this.shouldCrate,
            customProperties: this.customProperties.toObject(),
            version: this.version,
            summary: this.summary,
            skills: this.skills.toArray().map(item => item.toJS()),
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get description(): ?string {
        return this._state.get('description');
    }

    setDescription(description: ?string): this {
        return this.clone(this._state.set('description', description));
    }

    get parts(): Immutable.List<LineItemPart> {
        return this._state.get('parts');
    }

    setParts(parts: Immutable.List<LineItemPart>): this {
        return this.clone(this._state.set('parts', parts));
    }

    get scanId(): ?string {
        return this._state.get('scanId');
    }

    setScanId(scanId: ?string): this {
        return this.clone(this._state.set('scanId', scanId));
    }

    get category(): ?string {
        return this._state.get('category');
    }

    setCategory(category: ?string): this {
        return this.clone(this._state.set('category', category));
    }

    get imageUrl(): ?string {
        return this._state.get('imageUrl');
    }

    setImageUrl(imageUrl: ?string): this {
        return this.clone(this._state.set('imageUrl', imageUrl));
    }

    get locationIds(): Immutable.List<LocationId> {
        return this._state.get('locationIds');
    }

    setLocationIds(locationIds: Immutable.List<LocationId>): this {
        return this.clone(this._state.set('locationIds', locationIds));
    }

    get locations(): Immutable.List<MasterLocation> {
        return this._state.get('locations');
    }

    setLocations(locations: Immutable.List<MasterLocation>): this {
        return this.clone(this._state.set('locations', locations));
    }

    get instructions(): Immutable.List<string> {
        return this._state.get('instructions');
    }

    setInstructions(instructions: Immutable.List<string>): this {
        return this.clone(this._state.set('instructions', instructions));
    }

    get temperatureThreshold(): ?TemperatureThreshold {
        return this._state.get('temperatureThreshold');
    }

    setTemperatureThreshold(temperatureThreshold: ?TemperatureThreshold): this {
        return this.clone(
            this._state.set('temperatureThreshold', temperatureThreshold)
        );
    }

    get transactionDuration(): ?number {
        return this._state.get('transactionDuration');
    }

    setTransactionDuration(transactionDuration: ?number): this {
        return this.clone(
            this._state.set('transactionDuration', transactionDuration)
        );
    }

    get carton(): ?Carton {
        return this._state.get('carton');
    }

    setCarton(carton: ?Carton): this {
        return this.clone(this._state.set('carton', carton));
    }

    get shouldCrate(): boolean {
        return this._state.get('shouldCrate');
    }

    setShouldCrate(shouldCrate: boolean): this {
        return this.clone(this._state.set('shouldCrate', shouldCrate));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get summary(): SummaryType {
        return this._state.get('summary');
    }

    setSummary(summary: SummaryType): this {
        return this.clone(this._state.set('summary', summary));
    }

    get skills(): Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    setSkills(skills: Immutable.List<SkillId>): this {
        return this.clone(this._state.set('skills', skills));
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }
}
